// 063-breaking-views — Full-integer count formatter.
//
// Companion to `formatCompactNumber` (which abbreviates large counts as
// "1.2K"/"52K"/"1.5M"). Use `formatCount` on roomier surfaces where the full
// integer fits (e.g. inline thesis-reaction-button thumb counts); use
// `formatCompactNumber` on tight surfaces (e.g. the Breaking Views widget
// rows). See spec 063-breaking-views FR-025.
//
// Both formatters MUST `Math.round` their input — `<AnimatedNumber>` passes
// float values mid-animation (interpolated between previous and target),
// and counts must always display as integers.
//
// Per-locale `Intl.NumberFormat` instances are cached in a Map so the
// instance — and `formatCountFor(locale)` — keep a stable identity across
// renders for a given locale: passing a fresh inline arrow to
// `<AnimatedNumber>` would re-trigger animations on every render because the
// primitive's effect depends on `format` identity.

const DEFAULT_LOCALE = 'en-US';

const formatterByLocale: Map<string, Intl.NumberFormat> = new Map<string, Intl.NumberFormat>();
const fnByLocale: Map<string, (n: number) => string> = new Map<string, (n: number) => string>();

function getFormatter(locale: string): Intl.NumberFormat {
  let formatter = formatterByLocale.get(locale);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(locale);
    formatterByLocale.set(locale, formatter);
  }
  return formatter;
}

/** Format an integer count with locale separators (e.g. `1234` → `"1,234"`).
 *  Math.round'd because <AnimatedNumber> may pass float values mid-animation. */
export function formatCount(n: number, locale: string = DEFAULT_LOCALE): string {
  return getFormatter(locale).format(Math.round(n));
}

/** Stable per-locale `formatCount` for `<AnimatedNumber format={...}>`.
 *  The returned function has a stable identity per locale, so the primitive's
 *  format-identity effect does not re-trigger animations on every render. */
export function formatCountFor(locale: string = DEFAULT_LOCALE): (n: number) => string {
  let fn = fnByLocale.get(locale);
  if (fn === undefined) {
    fn = (n: number): string => formatCount(n, locale);
    fnByLocale.set(locale, fn);
  }
  return fn;
}
